/**
 * Theme utility functions for accessing theme values in React components
 * Import these when you need to use theme values in JavaScript (e.g., for canvas, charts, inline calculations)
 */

import { theme } from '../config/theme';

/**
 * Get a color value from the theme
 * @param {string} colorKey - Key from theme.colors (e.g., "primary", "background")
 * @returns {string} CSS hsl() value or undefined if key doesn't exist
 * 
 * @example
 * const primaryColor = getColor('primary'); // Returns "hsl(165 37% 22%)"
 */
export function getColor(colorKey) {
    const color = theme.colors[colorKey];
    return color ? `hsl(${color})` : undefined;
}

/**
 * Get a spacing value from the theme
 * @param {string} key - Key from theme.spacing (e.g., "md", "xl", "2xl")
 * @returns {string} CSS rem value or undefined if key doesn't exist
 * 
 * @example
 * const mediumSpacing = getSpacing('md'); // Returns "1rem"
 */
export function getSpacing(key) {
    return theme.spacing[key];
}

/**
 * Get a font size from the theme
 * @param {string} key - Key from theme.typography.fontSize (e.g., "sm", "lg", "2xl")
 * @returns {string} CSS rem value or undefined if key doesn't exist
 * 
 * @example
 * const largeFontSize = getFontSize('lg'); // Returns "1.125rem"
 */
export function getFontSize(key) {
    return theme.typography.fontSize[key];
}

/**
 * Get a font weight from the theme
 * @param {string} key - Key from theme.typography.fontWeight (e.g., "normal", "bold")
 * @returns {string} CSS font weight value or undefined if key doesn't exist
 * 
 * @example
 * const boldWeight = getFontWeight('bold'); // Returns "700"
 */
export function getFontWeight(key) {
    return theme.typography.fontWeight[key];
}

/**
 * Get a border radius value from the theme
 * @param {string} key - Key from theme.radius (e.g., "sm", "md", "lg", "DEFAULT")
 * @returns {string} CSS rem/px value or undefined if key doesn't exist
 * 
 * @example
 * const defaultRadius = getRadius('DEFAULT'); // Returns "0.35rem"
 */
export function getRadius(key = 'DEFAULT') {
    return theme.radius[key];
}

/**
 * Get a shadow value from the theme
 * @param {string} key - Key from theme.shadows (e.g., "sm", "md", "lg")
 * @returns {string} CSS box-shadow value or undefined if key doesn't exist
 * 
 * @example
 * const mediumShadow = getShadow('md'); // Returns "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)"
 */
export function getShadow(key) {
    return theme.shadows[key];
}

/**
 * Get a transition duration from the theme
 * @param {string} key - Key from theme.transitions (e.g., "fast", "base", "slow")
 * @returns {string} CSS duration value or undefined if key doesn't exist
 * 
 * @example
 * const baseDuration = getTransition('base'); // Returns "200ms"
 */
export function getTransition(key = 'base') {
    return theme.transitions[key];
}

/**
 * Get a z-index value from the theme
 * @param {string} key - Key from theme.zIndex (e.g., "modal", "tooltip", "dropdown")
 * @returns {string} z-index value or undefined if key doesn't exist
 * 
 * @example
 * const modalZIndex = getZIndex('modal'); // Returns "1050"
 */
export function getZIndex(key) {
    return theme.zIndex[key];
}

/**
 * Get the entire theme object
 * @returns {object} Complete theme configuration
 * 
 * @example
 * const fullTheme = getTheme();
 * console.log(fullTheme.colors.primary); // "165 37% 22%"
 */
export function getTheme() {
    return theme;
}

// Re-export theme parts for direct access
export const { colors, spacing, typography, radius, shadows, transitions, zIndex } = theme;
